import { Component, EventEmitter, Input, OnDestroy, OnInit, Optional, Output, Self } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NgControl, Validators, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import * as uuid from 'uuid';
import { BcitiTextSelectOption } from '../../forms/text-select/bciti-text-select.component';

@Component({
    selector: 'app-collection-schedule',
    templateUrl: './collection-schedule.component.html',
    styleUrls: ['./collection-schedule.component.scss'],
})
export class CollectionScheduleComponent implements OnInit, OnDestroy, ControlValueAccessor {
    destroy$ = new Subject();

    public onChange: (param?: any) => any;
    public onTouched: () => any;

    @Input() optionsFrequencies: BcitiTextSelectOption[] = [];
    @Input() optionsDays: any[] = [];
    @Input() closeable: boolean = false;
    @Input() disabled: boolean;
    @Input() id: string = uuid.v4();
    @Input() hasBorder: boolean = true;
    @Input() endDateIsRequired = true;

    @Output() cancelCreate = new EventEmitter();
    @Output('change') changeOutput = new EventEmitter<any>();

    scheduleForm: FormGroup = new FormGroup(
        {
            frequency: new FormControl('', [Validators.required]),
            from: new FormControl('', [Validators.required]),
            to: new FormControl(''),
            fromTime: new FormControl(''),
            days: new FormControl('', [Validators.required]),
        },
        { validators: [this.dateRangeValidator()], updateOn: 'change' },
    );

    startDateMin = new Date();
    endDateMin = new Date();
    scheduleDays = [];
    chipClass = 'day-chip';
    loading = false;

    // overlapError = false;

    private _value = {};

    constructor(@Self() @Optional() private ngControl: NgControl) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
    }

    public ngOnInit(): void {
        if (this.endDateIsRequired) {
            this.scheduleForm.controls.to.setValidators(Validators.required);
        }
        this.scheduleForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((v) => {
            this.value = v;
        });
    }

    get value(): any {
        return this._value;
    }

    set value(v) {
        this._value = v;
        if (this.onChange) {
            this.changeOutput.emit(v);
            this.onChange(v);
            this.onTouched();
        }
    }

    change(v) {
        this.value = v;
    }

    get invalid() {
        if (this.ngControl) {
            return this.ngControl.invalid;
        }
        return false;
    }

    get valid() {
        if (this.ngControl) {
            return this.ngControl.valid;
        }
        return false;
    }

    get pristine() {
        if (this.ngControl) {
            return this.ngControl.pristine;
        }
        return false;
    }

    get dirty() {
        if (this.ngControl) {
            return this.ngControl.dirty;
        }
        return false;
    }

    get touched() {
        if (this.ngControl) {
            return this.ngControl.touched;
        }
        return false;
    }

    writeValue(value: any): void {
        // the frequency FormControl is tied to bciti-text-select component.
        // Need to set the value as the reference to one of the options passed to it
        const formValue = { ...value, frequency: this.optionsFrequencies.find((of) => of.value === value.frequency) };
        this.scheduleForm.patchValue(formValue);
        this.scheduleForm.markAsUntouched();
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    registerOnChange(fn: any): void {
        // Store the provided function as an internal method.
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        // Store the provided function as an internal method.
        this.onTouched = fn;
    }

    cancelCreateAction() {
        this.cancelCreate.emit(this.id);
    }

    fromDateChange(e: Date) {
        this.scheduleForm.patchValue({ from: e });
        this.changeOutput.emit(e);
    }

    toDateChange(e: Date) {
        this.scheduleForm.patchValue({ to: e });
        this.changeOutput.emit(e);
    }

    dateRangeValidator(): ValidatorFn {
        return (form: FormGroup): ValidationErrors | null => {
            const from = form.get('from')?.value;
            const to = form.get('to')?.value;

            if (from && to && this.isDateOrString(from) && this.isDateOrString(to)) {
                const fromDate = new Date(from);
                const toDate = new Date(to);
                const isRangeValid = !isNaN(fromDate.getTime()) && !isNaN(toDate.getTime()) && toDate >= fromDate;
                return isRangeValid ? null : { dateRange: true };
            }

            return null;
        };
    }

    private isDateOrString(value: any): boolean {
        return value instanceof Date || typeof value === 'string';
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * GETTERS
     */
    get frequency() {
        return this.scheduleForm.get('frequency');
    }
    get from() {
        return this.scheduleForm.get('from');
    }
    get to() {
        return this.scheduleForm.get('to');
    }
    get fromTime() {
        return this.scheduleForm.get('fromTime');
    }
    get days() {
        return this.scheduleForm.get('days');
    }

    get formTouched() {
        return this.scheduleForm.touched;
    }
    get formValid() {
        return this.scheduleForm.valid;
    }
    get formInvalid() {
        return this.scheduleForm.invalid;
    }
    // get formOverlapError() { return this.scheduleForm.getError('overlap'); }
}
